// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, computeNextOccurrence, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import type {
  ScheduledTransaction,
  FrequencySpec,
  ScheduledTransactionCreatedPayload,
  ScheduledTransactionUpdatedPayload,
  ScheduledTransactionDeletedPayload,
  TransactionPostedPayload,
} from '../../types.js';

interface ScheduledTransactionsProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

const FREQ_LABEL_KEYS = {
  once: 'frequency.once', daily: 'frequency.daily', weekly: 'frequency.weekly', biweekly: 'frequency.biweekly',
  monthly: 'frequency.monthly', quarterly: 'frequency.quarterly', yearly: 'frequency.yearly',
} as const satisfies Record<FrequencySpec['type'], string>;

const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const labelCls = 'block text-xs text-text-muted mb-1';

export function ScheduledTransactions({ worker, keyData }: ScheduledTransactionsProps) {
  const { t } = useTranslation(['scheduled', 'common', 'errors', 'transactions']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [showEditor, setShowEditor] = useState(false);
  const [editingSt, setEditingSt] = useState<ScheduledTransaction | undefined>();

  const scheduledList = [...ledger.scheduledTransactions.values()].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const today = new Date().toISOString().slice(0, 10);

  async function handleToggleEnabled(st: ScheduledTransaction) {
    const payload: ScheduledTransactionUpdatedPayload = {
      scheduledTransactionId: st.id,
      enabled: !st.enabled,
    };
    await encryptAndPush(await buildEvent('ScheduledTransactionUpdated', payload, ledger.lastSequence + 1));
  }

  async function handleDelete(st: ScheduledTransaction) {
    if (!confirm(t('errors:confirmDeleteScheduled', { name: st.name }))) return;
    const payload: ScheduledTransactionDeletedPayload = { scheduledTransactionId: st.id };
    await encryptAndPush(await buildEvent('ScheduledTransactionDeleted', payload, ledger.lastSequence + 1));
  }

  async function handleRunNow(st: ScheduledTransaction) {
    const splits = st.templateTransaction.splits.map((s) => ({
      splitId: uuidv4(),
      accountId: s.accountId,
      amount: s.amount,
      quantity: s.quantity,
      reconcileState: 'n' as const,
      reconcileDate: null,
      memo: s.memo,
      action: s.action,
    }));
    const payload: TransactionPostedPayload = {
      transactionId: uuidv4(),
      date: today,
      dateEntered: new Date().toISOString(),
      description: st.templateTransaction.description,
      num: st.templateTransaction.num,
      notes: st.templateTransaction.notes,
      currency: st.templateTransaction.currency,
      splits,
    };
    try {
      validateSplitsBalance(splits);
      await encryptAndPush(await buildEvent('TransactionPosted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:createTransaction'));
    }
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('title')}</h2>
        <button
          onClick={() => { setEditingSt(undefined); setShowEditor(true); }}
          className="px-3 py-1.5 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          + {t('common:actions.new')}
        </button>
      </div>

      {scheduledList.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-text-subtle">
          <div className="text-center">
            <p className="text-base font-medium mb-1 text-text-muted">{t('empty.title')}</p>
            <p className="text-sm text-text-subtle">{t('empty.prompt')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-bg-surface border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-border-strong">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.name')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.frequency')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.nextDue')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.lastDate')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.active')}</th>
                <th className="text-right py-1.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {scheduledList.map((st) => {
                const next = st.frequency.nextOccurrence
                  ?? computeNextOccurrence(st.frequency, today);
                const isOverdue = next && next <= today;
                return (
                  <tr key={st.id} className="border-b border-border hover:bg-bg-elevated">
                    <td className="py-2 px-4 font-medium text-text">{st.name}</td>
                    <td className="py-2 px-4 text-text-muted">
                      {t(FREQ_LABEL_KEYS[st.frequency.type])}
                      {st.frequency.interval > 1 ? t('intervalSuffix', { interval: st.frequency.interval }) : ''}
                    </td>
                    <td className={`py-2 px-4 font-mono text-xs ${isOverdue ? 'text-negative font-semibold' : 'text-text'}`}>
                      {next ?? '—'}
                      {isOverdue && <span className="ml-1">{t('overdue')}</span>}
                    </td>
                    <td className="py-2 px-4 text-text-muted font-mono text-xs">
                      {st.frequency.lastOccurrence ?? '—'}
                    </td>
                    <td className="py-2 px-4">
                      <button
                        onClick={() => handleToggleEnabled(st)}
                        className={`relative inline-flex h-5 w-9 transition-colors ${
                          st.enabled ? 'bg-accent' : 'bg-text-subtle'
                        }`}
                        title={st.enabled ? t('enabledHint') : t('disabledHint')}
                      >
                        <span className={`inline-block h-4 w-4 bg-white shadow transform transition-transform mt-0.5 ${
                          st.enabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleRunNow(st)}
                          className="text-xs text-accent hover:underline"
                          title={t('runTitle')}
                        >
                          {t('run')}
                        </button>
                        <button
                          onClick={() => { setEditingSt(st); setShowEditor(true); }}
                          className="text-xs text-text-muted hover:text-text"
                        >
                          {t('common:actions.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(st)}
                          className="text-xs text-negative hover:underline"
                        >
                          {t('common:actions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showEditor && (
        <ScheduledTransactionEditor
          {...(editingSt !== undefined ? { existing: editingSt } : {})}
          worker={worker}
          keyData={keyData}
          onClose={() => { setShowEditor(false); setEditingSt(undefined); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

interface EditorProps {
  existing?: ScheduledTransaction;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

function ScheduledTransactionEditor({ existing, worker, keyData, onClose }: EditorProps) {
  const { t } = useTranslation(['scheduled', 'common', 'errors', 'transactions', 'mobile']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);
  const accounts = [...ledger.accounts.values()].filter((a) => !a.placeholder);

  const [tab, setTab] = useState<'overview' | 'frequency' | 'template'>('overview');
  const [name, setName] = useState(existing?.name ?? '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [autoCreate, setAutoCreate] = useState(existing?.autoCreate ?? false);
  const [advanceDays, setAdvanceDays] = useState(existing?.autoCreateDaysInAdvance ?? 0);
  const [reminderDays, setReminderDays] = useState(existing?.advanceReminderDays ?? 0);
  const [freqType, setFreqType] = useState<FrequencySpec['type']>(existing?.frequency.type ?? 'monthly');
  const [interval, setInterval] = useState(existing?.frequency.interval ?? 1);
  const [startDate, setStartDate] = useState(existing?.frequency.startDate ?? new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(existing?.frequency.endDate ?? '');
  const [tmplDesc, setTmplDesc] = useState(existing?.templateTransaction.description ?? '');
  const [tmplNotes, setTmplNotes] = useState(existing?.templateTransaction.notes ?? '');
  const [tmplCurrency, setTmplCurrency] = useState(existing?.templateTransaction.currency ?? 'USD');
  const [tmplSplits, setTmplSplits] = useState(
    existing?.templateTransaction.splits ?? [
      { splitId: uuidv4(), accountId: '', amount: 0, quantity: 0, memo: '', action: '' },
      { splitId: uuidv4(), accountId: '', amount: 0, quantity: 0, memo: '', action: '' },
    ],
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const nextOccurrences = useMemo(() => {
    const result: string[] = [];
    let after = new Date().toISOString().slice(0, 10);
    for (let i = 0; i < 5; i++) {
      const next = computeNextOccurrence({ type: freqType, interval, startDate, endDate: endDate || null }, after);
      if (!next) break;
      result.push(next);
      after = next;
    }
    return result;
  }, [freqType, interval, startDate, endDate]);

  function updateTmplSplit(idx: number, field: string, value: string | number) {
    setTmplSplits((prev) => prev.map((s, i) =>
      i === idx ? { ...s, [field]: value } : s,
    ));
  }

  async function handleSave() {
    if (!name.trim()) { setError(t('errors:nameRequired')); return; }
    setSaving(true);
    try {
      const freq: FrequencySpec = {
        type: freqType,
        interval,
        startDate,
        endDate: endDate || null,
        lastOccurrence: existing?.frequency.lastOccurrence ?? null,
        nextOccurrence: nextOccurrences[0] ?? null,
      };
      const template = {
        description: tmplDesc,
        num: '',
        notes: tmplNotes,
        currency: tmplCurrency,
        splits: tmplSplits,
      };

      if (existing) {
        const payload: ScheduledTransactionUpdatedPayload = {
          scheduledTransactionId: existing.id,
          name, enabled, autoCreate,
          autoCreateDaysInAdvance: advanceDays,
          advanceReminderDays: reminderDays,
          templateTransaction: template,
          frequency: freq,
        };
        await encryptAndPush(await buildEvent('ScheduledTransactionUpdated', payload, ledger.lastSequence + 1));
      } else {
        const payload: ScheduledTransactionCreatedPayload = {
          scheduledTransactionId: uuidv4(),
          name, enabled, autoCreate,
          autoCreateDaysInAdvance: advanceDays,
          advanceReminderDays: reminderDays,
          templateTransaction: template,
          frequency: freq,
        };
        await encryptAndPush(await buildEvent('ScheduledTransactionCreated', payload, ledger.lastSequence + 1));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('mobile:errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-md font-semibold text-text">
            {existing ? t('editTitle') : t('newTitle')}
          </h2>
          <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
        </div>

        <div className="flex border-b border-border">
          {(['overview', 'frequency', 'template'] as const).map((tabKey) => (
            <button
              key={tabKey}
              onClick={() => setTab(tabKey)}
              className={`px-6 py-2 text-sm border-b-2 transition-colors -mb-px capitalize ${
                tab === tabKey ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text'
              }`}
            >
              {t(`tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{error}</div>
          )}

          {tab === 'overview' && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t('form.nameLabel')}</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className={inputCls} placeholder={t('form.namePlaceholder')} autoFocus />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="border-border" />
                <span className="text-sm text-text">{t('form.enabled')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={autoCreate} onChange={(e) => setAutoCreate(e.target.checked)} className="border-border" />
                <span className="text-sm text-text">{t('form.autoCreate')}</span>
              </label>
              {autoCreate && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t('form.createDaysAdvance')}</label>
                    <input type="number" min={0} value={advanceDays} onChange={(e) => setAdvanceDays(Number(e.target.value))}
                      className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>{t('form.remindDaysAdvance')}</label>
                    <input type="number" min={0} value={reminderDays} onChange={(e) => setReminderDays(Number(e.target.value))}
                      className={inputCls} />
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'frequency' && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t('form.frequencyType')}</label>
                <select value={freqType} onChange={(e) => setFreqType(e.target.value as FrequencySpec['type'])}
                  className={inputCls}>
                  {(Object.keys(FREQ_LABEL_KEYS) as FrequencySpec['type'][]).map((value) => (
                    <option key={value} value={value}>{t(FREQ_LABEL_KEYS[value])}</option>
                  ))}
                </select>
              </div>
              {freqType !== 'once' && (
                <div>
                  <label className={labelCls}>{t('form.interval')}</label>
                  <input type="number" min={1} value={interval} onChange={(e) => setInterval(Number(e.target.value))}
                    className={inputCls} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('form.startDate')}</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t('form.endDate')}</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className={inputCls} />
                </div>
              </div>
              {nextOccurrences.length > 0 && (
                <div className="p-3 bg-bg border border-border">
                  <p className="text-xs font-medium text-text-muted mb-2 uppercase tracking-wide">{t('form.next5')}</p>
                  <ul className="space-y-1">
                    {nextOccurrences.map((d, i) => (
                      <li key={i} className="text-sm font-mono text-text">{d}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {tab === 'template' && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t('transactions:form.descriptionPayee')}</label>
                <input type="text" value={tmplDesc} onChange={(e) => setTmplDesc(e.target.value)}
                  className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t('form.notesOptional')}</label>
                <input type="text" value={tmplNotes} onChange={(e) => setTmplNotes(e.target.value)}
                  className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t('form.currency')}</label>
                <input type="text" value={tmplCurrency} onChange={(e) => setTmplCurrency(e.target.value)}
                  className="w-24 bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none" />
              </div>
              <div>
                <label className={labelCls}>{t('form.templateSplits')}</label>
                <div className="space-y-2">
                  {tmplSplits.map((s, idx) => (
                    <div key={s.splitId} className="flex items-center gap-2">
                      <select
                        value={s.accountId}
                        onChange={(e) => updateTmplSplit(idx, 'accountId', e.target.value)}
                        className="flex-1 bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                      >
                        <option value="">{t('transactions:form.selectAccount')}</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={(s.amount / 100).toFixed(2)}
                        onChange={(e) => updateTmplSplit(idx, 'amount', Math.round(parseFloat(e.target.value) * 100))}
                        className="w-28 bg-bg border border-border text-text text-sm px-2 py-1.5 text-right font-mono focus:border-accent focus:outline-none"
                        placeholder="0.00"
                      />
                      {tmplSplits.length > 2 && (
                        <button type="button" onClick={() => setTmplSplits((p) => p.filter((_, i) => i !== idx))}
                          className="text-text-muted hover:text-negative text-lg leading-none px-1">
                          &times;
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button"
                  onClick={() => setTmplSplits((p) => [...p, { splitId: uuidv4(), accountId: '', amount: 0, quantity: 0, memo: '', action: '' }])}
                  className="mt-2 text-sm text-accent hover:underline">
                  + {t('transactions:form.addSplit')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
            {t('common:actions.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors">
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
